import React, { useState, useEffect } from "react";
import {
    Box, Avatar, Typography, Card, CardContent,
    CircularProgress, Chip, Skeleton, Alert, IconButton, Tooltip,
} from "@mui/material";
import ArrowBackRoundedIcon     from "@mui/icons-material/ArrowBackRounded";
import PhoneRoundedIcon         from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon         from "@mui/icons-material/EmailOutlined";
import LocationOnRoundedIcon    from "@mui/icons-material/LocationOnRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import FmdGoodRoundedIcon       from "@mui/icons-material/FmdGoodRounded";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import PersonRoundedIcon        from "@mui/icons-material/PersonRounded";
import Inventory2RoundedIcon    from "@mui/icons-material/Inventory2Rounded";
import { decryptId } from "../helper/idCodec.js";
import { useParams, useNavigate } from "react-router-dom";
import { getData, API_ENDPOINTS } from "../../services/apiService.js";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
    orange:  "#FF7900", orangeLt: "#FFF0E0",
    emerald: "#059669", emeraldLt: "#D1FAE5",
    rose:    "#E11D48", roseLt:   "#FFE4E6",
    indigo:  "#4F46E5", indigoLt: "#EEF2FF",
    amber:   "#D97706", amberLt:  "#FEF3C7",
    bg:      "#F7FAFC", border: "#E2E8F0",
    text:    "#0F172A", muted:  "#64748B",
};

const AVATAR_PALETTE = [
    [T.tealLt, T.teal], [T.orangeLt, T.orange], [T.indigoLt, T.indigo],
    [T.emeraldLt, T.emerald], [T.amberLt, T.amber], [T.roseLt, T.rose],
];
const avatarBg = (name = "") => AVATAR_PALETTE[name.charCodeAt(0) % AVATAR_PALETTE.length];
const initials = (name = "") => { const p = name.trim().split(" "); return p.length >= 2 ? (p[0][0]+p[1][0]).toUpperCase() : (p[0]?.[0] ?? "?").toUpperCase(); };
const fmtDate  = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
const fmtRs    = n => n != null ? `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—";

// ── Package status config ─────────────────────────────────────────────────
const PKG_STATUS = {
    Delivered: { bg: T.emeraldLt, color: T.emerald },
    Accepted:  { bg: T.tealLt,   color: T.teal    },
    Picked:    { bg: T.indigoLt, color: T.indigo  },
    Requested: { bg: T.amberLt,  color: T.amber   },
    Archived:  { bg: "#F1F5F9",  color: T.muted   },
    default:   { bg: "#F1F5F9",  color: T.muted   },
};

const SCard = ({ children, sx = {} }) => (
    <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)", ...sx }}>
        {children}
    </Card>
);

const SHead = ({ icon, title }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: T.tealLt, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {React.cloneElement(icon, { sx: { fontSize: 15, color: T.teal } })}
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</Typography>
    </Box>
);

const InfoRow = ({ icon, label, value }) => (
    <Box sx={{ display: "flex", alignItems: "center", py: 1.2, borderBottom: `1px solid ${T.border}`, "&:last-child": { borderBottom: "none" }, gap: 1.5 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: "7px", bgcolor: T.bg, border: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {React.cloneElement(icon, { sx: { fontSize: 14, color: T.muted } })}
        </Box>
        <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.muted, textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text, mt: 0.1 }}>{value || "—"}</Typography>
        </Box>
    </Box>
);

// ── Package card ──────────────────────────────────────────────────────────
const PkgCard = ({ pkg }) => {
    const cfg = PKG_STATUS[pkg.status] || PKG_STATUS.default;
    const fromCity = pkg.pickup_point?.city || pkg.pickup_point?.point || "N/A";
    const toCity   = pkg.drop_point?.city   || pkg.drop_point?.point   || "N/A";

    return (
        <Card sx={{
            borderRadius: "14px", border: `1px solid ${T.border}`,
            boxShadow: "0 1px 3px rgba(15,23,42,0.05)", overflow: "hidden",
            transition: "transform 0.15s, box-shadow 0.15s",
            "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 16px rgba(0,124,132,0.10)" },
        }}>
            <Box sx={{ height: 3, background: `linear-gradient(90deg, ${cfg.color}, ${cfg.color}66)` }} />
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                {/* Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text }}>{pkg.package_name || `Package #${pkg.package_id}`}</Typography>
                        <Typography sx={{ fontSize: 11, color: T.muted, mt: 0.2 }}>Ref: {pkg.reference_id || "—"}</Typography>
                    </Box>
                    <Chip label={pkg.status} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, borderRadius: "6px", bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}33` }} />
                </Box>

                {/* Route */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1 }}>
                    <FmdGoodRoundedIcon sx={{ fontSize: 13, color: T.teal }} />
                    <Typography sx={{ fontSize: 12, color: T.text, fontWeight: 500 }}>{fromCity}</Typography>
                    <Box sx={{ flex: 1, height: 1, bgcolor: T.border, mx: 0.5 }} />
                    <Box sx={{ width: 0, height: 0, borderTop: "3px solid transparent", borderBottom: "3px solid transparent", borderLeft: `5px solid ${T.muted}` }} />
                    <FmdGoodRoundedIcon sx={{ fontSize: 13, color: T.rose }} />
                    <Typography sx={{ fontSize: 12, color: T.text, fontWeight: 500 }}>{toCity}</Typography>
                </Box>

                {/* Truck */}
                {pkg.truck && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.8 }}>
                        <LocalShippingRoundedIcon sx={{ fontSize: 13, color: T.muted }} />
                        <Typography sx={{ fontSize: 12, color: T.muted }}>
                            {pkg.truck.truck_name || "—"} · {pkg.truck.truck_no}
                        </Typography>
                    </Box>
                )}

                {/* Payment + date */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, pt: 1, borderTop: `1px solid ${T.border}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                        <CurrencyRupeeRoundedIcon sx={{ fontSize: 13, color: T.emerald }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.emerald }}>
                            {fmtRs(pkg.payment?.amount)}
                            {pkg.payment?.status && <Box component="span" sx={{ fontSize: 10, color: T.muted, ml: 0.5 }}>({pkg.payment.status})</Box>}
                        </Typography>
                    </Box>
                    {pkg.pickup_date && (
                        <Typography sx={{ fontSize: 11, color: T.muted }}>
                            Pickup {fmtDate(pkg.pickup_date)}
                        </Typography>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

// ── Main component ────────────────────────────────────────────────────────
export default function UserDetails() {
    const { id }     = useParams();
    const navigate   = useNavigate();
    const userId     = id ? decryptId(id) : "";

    const [customer, setCustomer] = useState(null);
    const [packages, setPackages] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [pkgLoad,  setPkgLoad]  = useState(true);
    const [error,    setError]    = useState(null);

    useEffect(() => {
        if (!userId) return;
        getData(API_ENDPOINTS.findCustomerById, { customer_id: userId })
            .then(res => { if (res.statusCode === 200) setCustomer(res.result?.customer_details); else setError("Failed to load customer."); })
            .catch(() => setError("Failed to load customer."))
            .finally(() => setLoading(false));
    }, [userId]);

    useEffect(() => {
        if (!userId) return;
        getData(API_ENDPOINTS.fetchPackagesByCustomer, { customer_id: userId, page: 1, limit: 100 })
            .then(res => { if (res.statusCode === 200) setPackages(res.result?.packages || []); })
            .catch(() => { /* non-fatal */ })
            .finally(() => setPkgLoad(false));
    }, [userId]);

    if (loading) return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", p: 3 }}>
            <Skeleton variant="rounded" height={160} sx={{ borderRadius: "20px", mb: 3 }} />
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
                {[1,2,3].map(i => <Skeleton key={i} variant="rounded" height={160} sx={{ borderRadius: "16px" }} />)}
            </Box>
        </Box>
    );

    if (error || !customer) return <Alert severity="error" sx={{ m: 3 }}>{error || "Customer not found."}</Alert>;

    const fullName  = `${customer.first_name || ""} ${customer.last_name || ""}`.trim();
    const [avatarBgColor, avatarFgColor] = avatarBg(fullName);
    const location  = [customer.city, customer.state].filter(Boolean).join(", ");

    const deliveredCount = packages.filter(p => p.status === "Delivered").length;
    const activeCount    = packages.filter(p => ["Accepted","Picked","Requested"].includes(p.status)).length;

    return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", pb: 4 }}>

            {/* ── Hero header ── */}
            <Box sx={{
                background: `linear-gradient(135deg, ${T.teal} 0%, ${T.tealDk} 100%)`,
                borderRadius: "20px", p: 3, mb: 3,
                boxShadow: "0 4px 20px rgba(0,124,132,0.2)",
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                    <IconButton onClick={() => navigate("/app/users")}
                        sx={{ bgcolor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "10px", color: "#fff", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
                        <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>Customers</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, flexWrap: "wrap" }}>
                    <Avatar src={customer.pic} sx={{ width: 68, height: 68, bgcolor: avatarBgColor, color: avatarFgColor, fontSize: 22, fontWeight: 800, border: "2px solid rgba(255,255,255,0.35)" }}>
                        {!customer.pic && initials(fullName)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#fff", fontFamily: "'Plus Jakarta Sans', sans-serif", mb: 0.5 }}>
                            {fullName || "Unknown Customer"}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, bgcolor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", px: 1.2, py: 0.3 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>Customer #{userId}</Typography>
                            </Box>
                            {customer.mobile_num && <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>{customer.mobile_num}</Typography>}
                            {location && <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }}>{location}</Typography>}
                            <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.65)" }}>Joined {fmtDate(customer.createdAt)}</Typography>
                        </Box>
                    </Box>

                    {/* Package stats */}
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        {[
                            { label: "Total",     value: packages.length, bg: "rgba(255,255,255,0.15)", color: "#fff" },
                            { label: "Delivered", value: deliveredCount,   bg: T.emeraldLt, color: T.emerald },
                            { label: "Active",    value: activeCount,      bg: T.amberLt,   color: T.amber   },
                        ].map(s => (
                            <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 0.8, bgcolor: s.bg, borderRadius: "10px", px: 1.5, py: 0.7, border: s.color === "#fff" ? "1px solid rgba(255,255,255,0.25)" : `1px solid ${s.color}22` }}>
                                <Typography sx={{ fontSize: 16, fontWeight: 800, color: s.color, lineHeight: 1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.value}</Typography>
                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: s.color, opacity: 0.85 }}>{s.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* ── Profile info cards ── */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2, mb: 3 }}>
                <SCard>
                    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <SHead icon={<PersonRoundedIcon />} title="Contact" />
                        <InfoRow icon={<PhoneRoundedIcon />}           label="Mobile"    value={customer.mobile_num} />
                        <InfoRow icon={<EmailRoundedIcon />}           label="Email"     value={customer.email || "Not provided"} />
                        <InfoRow icon={<LocationOnRoundedIcon />}      label="Location"  value={location || "Not provided"} />
                        <InfoRow icon={<CalendarMonthRoundedIcon />}   label="Joined"    value={fmtDate(customer.createdAt)} />
                    </CardContent>
                </SCard>

                <SCard>
                    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <SHead icon={<Inventory2RoundedIcon />} title="Shipping Summary" />
                        {[
                            { label: "Total Bookings",  value: packages.length, color: T.teal   },
                            { label: "Delivered",       value: deliveredCount,  color: T.emerald },
                            { label: "Active",          value: activeCount,     color: T.amber   },
                            { label: "Archived",        value: packages.filter(p => p.status === "Archived").length, color: T.muted },
                        ].map(s => (
                            <Box key={s.label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.2, borderBottom: `1px solid ${T.border}`, "&:last-child": { borderBottom: "none" } }}>
                                <Typography sx={{ fontSize: 12, color: T.muted }}>{s.label}</Typography>
                                <Box sx={{ px: 1.2, py: 0.3, bgcolor: s.color + "18", borderRadius: "7px" }}>
                                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: s.color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.value}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </CardContent>
                </SCard>

                <SCard>
                    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <SHead icon={<CurrencyRupeeRoundedIcon />} title="Spend Summary" />
                        {(() => {
                            const totalSpend  = packages.reduce((s, p) => s + (Number(p.payment?.amount) || 0), 0);
                            const paidCount   = packages.filter(p => p.payment?.status === "paid").length;
                            return (
                                <>
                                    <Box sx={{ textAlign: "center", py: 2 }}>
                                        <Typography sx={{ fontSize: 28, fontWeight: 800, color: T.teal, fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1 }}>
                                            {fmtRs(totalSpend)}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: T.muted, mt: 0.5 }}>total across all bookings</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", pt: 1, borderTop: `1px solid ${T.border}` }}>
                                        <Typography sx={{ fontSize: 12, color: T.muted }}>Paid transactions</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.emerald }}>{paidCount}</Typography>
                                    </Box>
                                </>
                            );
                        })()}
                    </CardContent>
                </SCard>
            </Box>

            {/* ── Packages section ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Booking History</Typography>
                {!pkgLoad && <Box sx={{ px: "10px", py: "2px", bgcolor: T.tealLt, borderRadius: "7px", fontSize: 12, fontWeight: 700, color: T.teal }}>{packages.length}</Box>}
            </Box>

            {pkgLoad ? (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 2 }}>
                    {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} variant="rounded" height={160} sx={{ borderRadius: "14px" }} />)}
                </Box>
            ) : packages.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8 }}>
                    <Inventory2RoundedIcon sx={{ fontSize: 48, color: T.border, mb: 1 }} />
                    <Typography sx={{ color: T.muted, fontSize: 14 }}>No packages booked yet.</Typography>
                </Box>
            ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 2 }}>
                    {packages.map(pkg => <PkgCard key={pkg.mapping_id || pkg.package_id} pkg={pkg} />)}
                </Box>
            )}
        </Box>
    );
}
